import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckIcon, ChevronDownIcon, MagnifyingGlassIcon } from '@radix-ui/react-icons';
import * as RPNInput from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';
import type { Country, Value as PhoneValue } from 'react-phone-number-input';
import { isOutsideAnchors, subscribeDropdownPosition } from '../../utils/dropdownPortal';
import './PhoneInput.scss';

export interface PhoneInputProps
  extends Omit<
    React.ComponentProps<'input'>,
    'onChange' | 'value' | 'ref' | 'type'
  > {
  label?: string;
  value?: PhoneValue;
  onChange?: (value: PhoneValue) => void;
  error?: string;
  fullWidth?: boolean;
  defaultCountry?: Country;
  international?: boolean;
}

type CountryEntry = { label: string; value: Country | undefined };

type CountrySelectProps = {
  disabled?: boolean;
  value: Country;
  options: CountryEntry[];
  onChange: (country: Country) => void;
};

type CountrySelectOptionProps = RPNInput.FlagProps & {
  selectedCountry: Country;
  onChange: (country: Country) => void;
  onSelectComplete: () => void;
};

const FlagComponent = ({ country, countryName }: RPNInput.FlagProps) => {
  const Flag = flags[country];

  return (
    <span className="phone-input__flag" aria-hidden="true">
      {Flag && <Flag title={countryName} />}
    </span>
  );
};

const CountrySelectOption = ({
  country,
  countryName,
  selectedCountry,
  onChange,
  onSelectComplete,
}: CountrySelectOptionProps) => {
  const handleSelect = () => {
    onChange(country);
    onSelectComplete();
  };

  return (
    <button
      type="button"
      className="phone-input__country-option"
      onClick={handleSelect}
    >
      <FlagComponent country={country} countryName={countryName} />
      <span className="phone-input__country-option-name">{countryName}</span>
      <span className="phone-input__country-option-code">
        {`+${RPNInput.getCountryCallingCode(country)}`}
      </span>
      <CheckIcon
        className={`phone-input__country-option-check ${
          country === selectedCountry ? 'phone-input__country-option-check--visible' : ''
        }`}
        aria-hidden="true"
      />
    </button>
  );
};

const CountrySelect = ({
  disabled,
  value: selectedCountry,
  options: countryList,
  onChange,
}: CountrySelectProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    maxHeight: number;
    placement: 'top' | 'bottom';
  } | null>(null);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearchValue('');
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOutsideAnchors(event.target as Node, [
          containerRef.current,
          dropdownRef.current,
        ])
      ) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, closeDropdown]);

  useEffect(() => {
    if (!isOpen || !triggerRef.current) {
      setDropdownPosition(null);
      return;
    }

    return subscribeDropdownPosition(triggerRef.current, (position) => {
      setDropdownPosition({
        top: position.top,
        bottom: position.bottom,
        left: position.left,
        maxHeight: position.maxHeight,
        placement: position.placement,
      });
    });
  }, [isOpen]);

  const normalizedSearch = searchValue.trim().toLowerCase();
  const filteredCountries = countryList.filter(({ value, label }) => {
    if (!value) return false;
    if (!normalizedSearch) return true;

    const callingCode = `+${RPNInput.getCountryCallingCode(value)}`;
    return (
      label.toLowerCase().includes(normalizedSearch) ||
      value.toLowerCase().includes(normalizedSearch) ||
      callingCode.includes(normalizedSearch)
    );
  });

  const dropdown =
    isOpen && dropdownPosition
      ? createPortal(
          <div
            ref={dropdownRef}
            className={`phone-input__country-dropdown phone-input__country-dropdown--portal phone-input__country-dropdown--${dropdownPosition.placement}`}
            role="listbox"
            style={{
              top: dropdownPosition.top,
              bottom: dropdownPosition.bottom,
              left: dropdownPosition.left,
              maxHeight: dropdownPosition.maxHeight,
            }}
          >
            <div className="phone-input__country-search">
              <MagnifyingGlassIcon aria-hidden="true" />
              <input
                type="text"
                value={searchValue}
                placeholder="Search country..."
                onChange={(event) => setSearchValue(event.target.value)}
                autoFocus
              />
            </div>

            <div className="phone-input__country-list">
              {filteredCountries.length === 0 ? (
                <p className="phone-input__country-empty">No country found.</p>
              ) : (
                filteredCountries.map(({ value, label }) =>
                  value ? (
                    <CountrySelectOption
                      key={value}
                      country={value}
                      countryName={label}
                      selectedCountry={selectedCountry}
                      onChange={onChange}
                      onSelectComplete={closeDropdown}
                    />
                  ) : null
                )
              )}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div
      ref={containerRef}
      className={`phone-input__country-select ${isOpen ? 'phone-input__country-select--open' : ''}`}
    >
      <button
        ref={triggerRef}
        type="button"
        className="phone-input__country-trigger"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => {
          if (disabled) return;
          setIsOpen((open) => {
            if (open) setSearchValue('');
            return !open;
          });
        }}
      >
        <FlagComponent country={selectedCountry} countryName={selectedCountry} />
        <ChevronDownIcon className="phone-input__country-chevron" aria-hidden="true" />
      </button>

      {dropdown}
    </div>
  );
};

const InputComponent = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<'input'>
>(({ className = '', ...props }, ref) => (
  <input
    ref={ref}
    type="tel"
    className={`phone-input__number ${className}`.trim()}
    {...props}
  />
));
InputComponent.displayName = 'PhoneInputNumber';

export const PhoneInput = React.forwardRef<
  React.ElementRef<typeof RPNInput.default>,
  PhoneInputProps
>(
  (
    {
      label,
      value,
      onChange,
      error,
      required,
      disabled,
      fullWidth = true,
      className = '',
      id,
      defaultCountry = 'IN',
      international = true,
      placeholder = 'Contact Number',
      onBlur,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const errorId = error ? `${inputId}-error` : undefined;

    return (
      <div
        className={[
          'phone-input-wrapper',
          fullWidth && 'phone-input-wrapper--full-width',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {label && (
          <label htmlFor={inputId} className="phone-input-label">
            {label}
            {required && <span className="phone-input-required">*</span>}
          </label>
        )}

        <div
          className={[
            'phone-input',
            error && 'phone-input--error',
            disabled && 'phone-input--disabled',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <RPNInput.default
            {...props}
            id={inputId}
            ref={ref}
            className="phone-input__control"
            flagComponent={FlagComponent}
            countrySelectComponent={CountrySelect}
            inputComponent={InputComponent}
            smartCaret={false}
            international={international}
            defaultCountry={defaultCountry}
            value={value || undefined}
            disabled={disabled}
            required={required}
            placeholder={placeholder}
            aria-invalid={error ? true : undefined}
            aria-describedby={errorId}
            onBlur={onBlur}
            onChange={(nextValue) => onChange?.(nextValue || ('' as PhoneValue))}
          />
        </div>

        {error && (
          <span id={errorId} className="phone-input-error" role="alert">
            {error}
          </span>
        )}
      </div>
    );
  }
);

PhoneInput.displayName = 'PhoneInput';
